
import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { CustomEase } from 'gsap/dist/CustomEase';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import Vue from 'vue';
import { TransformedCmStage } from '~/transforms/transformModules';
import { TransformedArticlePageStage } from '~/transforms/transformPages';

gsap.registerPlugin(SplitText, ScrollTrigger, CustomEase);
CustomEase.create('curveNatural', '.4,0,.2,1');
CustomEase.create('curveAppear', '.03,0,.2,1');
CustomEase.create('curveTech', '.2,0,.8,1');

export default Vue.extend({
  props: {
    data: {
      type: Object as () => TransformedCmStage['data'] | TransformedArticlePageStage,
      required: true,
    },
  },
  mounted() {
    this.initAnimation();
    const navInline = document.querySelector<HTMLElement>('header');
    if (navInline && navInline.querySelector('.c-nav-inline')) {
      (this.$refs.stage as HTMLElement).classList.add('cm-stage--nav');
    }
  },
  computed: {
    isMinimal() {
      return !this.data.video && !this.data.button && !this.data.image && !this.data.copy && !this.data.news;
    },
    alertTheme() {
      if (this.$marketSettings.alertThemeOverride || this.alwaysShowAlerts) {
        return 'light';
      }
      // Use white theme for green stages and green theme for white stages
      return this.data.theme === 'green' ? 'white' : 'green';
    },
    alwaysShowAlerts() {
      return ['neuigkeiten', 'news', 'stiri'].includes(this.$route.params.pathMatch.replace('/', ''));
    },
  },
  methods: {
    initAnimation() {
      if (!process.client) {
        return;
      }

      gsap.config({ nullTargetWarn: false });

      const module = this.$el as HTMLElement;
      gsap.to(module, { opacity: 1, duration: 0.4, ease: 'curveAppear' });

      let copyMoveX;
      let copyMoveY;
      if (!module.classList.contains('cm-stage--rich')) {
        copyMoveX = 0;
        copyMoveY = 20;
        gsap.fromTo(
          ['.cm-stage__image .c-image', '.cm-stage__video .c-video'],
          { clipPath: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)' },
          {
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
            duration: 0.6,
            ease: 'curveTech',
          }
        );
      } else {
        copyMoveX = module.offsetWidth / 3;
        copyMoveY = 0;
        gsap.set(['.cm-stage__image .c-image', '.cm-stage__video .c-video'], {
          rotation: 90,
          transformOrigin: '100% 100%',
        });
        gsap.to(['.cm-stage__image .c-image', '.cm-stage__video .c-video'], {
          rotation: 0,
          duration: 0.5,
          ease: 'curveTech',
          delay: 0.15,
        });

        gsap.set(['.cm-stage__image .c-image__wrapper', '.cm-stage__video video'], {
          rotation: -90,
          transformOrigin: '100% 100%',
        });
        gsap.to(['.cm-stage__image .c-image__wrapper', '.cm-stage__video video'], {
          rotation: 0,
          duration: 0.5,
          ease: 'curveTech',
          delay: 0.15,
        });

        gsap.set(['.cm-stage__image img'], {
          scale: 1.2,
          transformOrigin: 'center center',
        });
        gsap.to(['.cm-stage__image img'], {
          scale: 1.0,
          duration: 0.8,
          ease: 'curveNatural',
          delay: 0.15,
        });
      }

      // ---> headline / copy / button

      const headline = this.$el.querySelector('.cm-stage__headline span') as HTMLElement;
      const headlineLines = new SplitText(headline, { type: 'lines' }).lines;

      gsap.set([headlineLines, '.cm-stage__copy', '.cm-stage__news', '.cm-stage__project'], {
        autoAlpha: 0,
        x: '+=' + copyMoveX,
        y: '+=' + copyMoveY,
      });
      gsap.to([headlineLines, '.cm-stage__copy', '.cm-stage__news', '.cm-stage__project'], {
        autoAlpha: 1,
        x: '-=' + copyMoveX,
        y: '-=' + copyMoveY,
        stagger: 0.1,
        duration: 0.6,
        ease: 'curveAppear',
        delay: 0,
      });

      gsap.set(['.cm-stage__button'], { autoAlpha: 0, y: '+=10' });
      gsap.to(['.cm-stage__button'], {
        autoAlpha: 1,
        y: '-=10',
        duration: 0.5,
        ease: 'curveAppear',
        delay: 0.6,
      });

      // ---> shape
      gsap.set(['.cm-stage__shape g path'], { strokeWidth: 0, x: -30, y: -30 });
      gsap.to(['.cm-stage__shape g path'], {
        strokeWidth: 30,
        x: 0,
        y: 0,
        duration: 0.5,
        ease: 'curveTech',
        delay: 1.2,
      });

      const shape = this.$el.querySelector<HTMLElement>('.cm-stage__shape');
      ScrollTrigger.saveStyles(shape);

      if (shape) {
        const mm = gsap.matchMedia();
        mm.add('(max-width: 1023px)', () => {
          gsap.set(shape, { y: () => -shape.getBoundingClientRect().height * 1.5 });
          gsap.fromTo(
            shape,
            { y: () => -shape.getBoundingClientRect().height * 1.5 },
            {
              y: () => 0,
              ease: 'none',
              scrollTrigger: {
                trigger: module,
                start: 'top top',
                end: 'bottom top',
                scrub: 0.8,
                invalidateOnRefresh: true,
              },
            }
          );
        });
        mm.add('(min-width: 1024px)', () => {
          gsap.set(shape, { y: () => -shape.getBoundingClientRect().height / 3 });
          gsap.fromTo(
            shape,
            { y: () => -shape.getBoundingClientRect().height / 3 },
            {
              y: () => shape.getBoundingClientRect().height / 2,
              ease: 'none',
              scrollTrigger: {
                trigger: module,
                start: 'top top',
                end: 'bottom center',
                scrub: 0.8,
                invalidateOnRefresh: true,
              },
            }
          );
        });
      }
    },
  },
});
